'use client'
import { Breadcrumbs as WebUIBreadcrumbs } from '@lastbrand/web-ui/breadcrumbs'
import { Skeleton } from '@lastbrand/web-ui/skeleton'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useCallback, useMemo } from 'react'

import TrackedLink from '@src/quince-components/src/atoms/link'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import getB2BRoute from '@src/quince-site-common/src/utils/b2b/get-b2b-route'

import type { BreadcrumbsProps } from './breadcrumbs-ds.types'
import type { RenderLink } from '@lastbrand/web-ui/breadcrumbs'

// Loading placeholder for the crumbs. Reproduces legacy's `.breadCrumbLoader`
// (breadCrumb.module.scss): `height: 1.6rem` against the legacy ~10px fluid root, i.e. ~16px.
//
// Spacing tokens must be referenced through `token(...)` here, and the responsive key must be `lg`:
//   - Panda resolves `height`/`width` against the `sizes` scale, which has no `space-*` members
//     (those live on `spacing`). A bare `space-16` therefore passes through as the literal
//     `height: space-16`, which browsers drop — Skeleton ships no default height, so the bar
//     collapses to 0. `token(spacing.space-16)` compiles to `var(--web-ui-spacing-space-16)`,
//     which resolves. Same trap as STRFRNT-16018 (locale-switcher-form-ds.tsx).
//   - `lg` is this design system's only breakpoint (valid keys: base / lg / lgOnly / lgDown).
//     An `md` key is emitted as a top-level `md: <value>` declaration with no enclosing media
//     query, so it never applies at any viewport.
// Neither mistake is a type error: the generated style-prop types end in a permissive string.
const breadcrumbLoaderCss = {
  // eslint-disable-next-line i18next/no-literal-string -- Panda spacing token reference (not user-facing copy)
  h: 'token(spacing.space-16)',
  w: {
    // eslint-disable-next-line i18next/no-literal-string -- Panda sizes token (not user-facing copy)
    base: 'full',
    // eslint-disable-next-line i18next/no-literal-string -- Panda spacing token reference (not user-facing copy)
    lg: 'token(spacing.space-120)',
  },
}

export function BreadcrumbsDS({
  directory,
  showLoader,
  deviceType,
  ariaLabel: ariaLabelProp,
  css: cssProp,
  isPDP,
}: BreadcrumbsProps) {
  const { t } = useTranslation('common')
  const { isB2BMode } = useStorefrontModeContext()

  const ariaLabel = ariaLabelProp ?? t('breadcrumbs_aria', { defaultValue: 'Breadcrumbs' })

  // Legacy parity (legacy-bread-crumb.tsx): the tracking `value` is the pre-B2B-transform
  // `path.route` even though the link href is B2B-transformed, so Mixpanel values match
  // legacy in B2B mode (STRFRNT-16873). renderLink only receives the href, so keep a lookup.
  const { items, routeByHref } = useMemo(() => {
    const paths = directory.paths
    const routeByHref = new Map<string, string>()
    const items = paths.map((path, index) => {
      const isLast = index === paths.length - 1
      const href = isLast ? undefined : isB2BMode ? getB2BRoute(path.route) : path.route
      if (href !== undefined) {
        routeByHref.set(href, path.route)
      }
      return {
        label: path.displayName,
        href,
      }
    })
    return { items, routeByHref }
  }, [directory.paths, isB2BMode])

  const renderLink: RenderLink = useCallback(
    ({ href, label, className }) => {
      return (
        <TrackedLink
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- RenderLink params are `any` when web-ui subpath can't be resolved by the eslint resolver
          href={href}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- RenderLink params are `any` when web-ui subpath can't be resolved by the eslint resolver
          className={className}
          withoutBaseStyle
          hidePointerCursor
          trackConfig={{
            trackInternal: true,
            category: 'navigation',
            // @i18n-ignore
            action: 'click_bread_crumb',
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- label/href are `any` when web-ui subpath can't be resolved by the eslint resolver
            label,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument -- label/href are `any` when web-ui subpath can't be resolved by the eslint resolver
            value: routeByHref.get(href) ?? href,
          }}
        >
          {label}
        </TrackedLink>
      )
    },
    [routeByHref],
  )

  const rootClass = useMemo(
    () =>
      // Breadcrumb alignment funnels on surface + viewport: only the CLP mobile web
      // crumb is centered; every other case — CLP desktop, and PDP on mobile or
      // desktop — aligns start (left). Expressed as a Panda breakpoint condition
      // (center on `base`, flex-start from `lg`) rather than UA sniffing. We keep the
      // legacy `text-transform: capitalize` on `nav a / nav span` because the current
      // breadcrumb labels come pre-formatted lowercased from the API and rely on this
      // rule for the visible casing; revisit when labels are sent in their final
      // display casing.
      css({
        '& nav a, & nav span': {
          // eslint-disable-next-line i18next/no-literal-string -- CSS keyword, not user-facing copy
          textTransform: 'capitalize',
        },
        // eslint-disable-next-line i18next/no-literal-string -- Panda layout tokens (not user-facing copy)
        display: 'flex',
        justifyContent: isPDP ? 'flex-start' : { base: 'center', lg: 'flex-start' },
        alignItems: 'center',
        mx: 0,
        // Mobile gutter only on the bare PDP-gallery path; the widget path is already gutter-wrapped
        // by SectionContentContainer, so leaking px/py there double-insets and misaligns the crumb.
        px: isPDP ? { base: 'space-12', lg: 0 } : 0,
        // space-8, not space-12: the mobile PDP PageTitle strip is 40px per Figma (STRFRNT-16793).
        py: isPDP ? { base: 'space-8', lg: 0 } : 0,
      }),
    [isPDP],
  )

  const navCss = useMemo(
    () =>
      css.raw(
        {
          // web-ui clips the nav (`overflow: hidden`), cutting the link's 6px focus-ring reach.
          // Pad the clip box by it, cancel that with a negative margin, and raise web-ui's own
          // `maxWidth: 100%` by the 12px the padding takes out of the shared border box.
          // eslint-disable-next-line i18next/no-literal-string -- Panda spacing token (not user-facing copy)
          padding: 'space-6',
          // eslint-disable-next-line i18next/no-literal-string -- Panda spacing token (not user-facing copy)
          margin: '-space-6',
          // eslint-disable-next-line i18next/no-literal-string -- CSS length expression (not user-facing copy)
          maxWidth: 'calc(100% + var(--web-ui-spacing-space-12))',
        },
        // eslint-disable-next-line i18next/no-literal-string -- Panda text style token (not user-facing copy)
        deviceType === DEVICE_TYPES.DESKTOP
          ? {
              // Figma PDP crumb is body-12 (16px line) — that keeps the gallery's breadcrumb zone at
              // the 48px the form column's space-48 top margin compensates for. CLP looks like
              // body-12 in Figma too, but it shipped reviewed at body-14, so it stays until re-spec'd.
              // eslint-disable-next-line i18next/no-literal-string -- Panda text style token (not user-facing copy)
              textStyle: isPDP ? 'body-12' : 'body-14',
            }
          : undefined,
        cssProp,
      ),
    [cssProp, deviceType, isPDP],
  )

  if (showLoader) {
    return <Skeleton aria-hidden css={breadcrumbLoaderCss} />
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- WebUIBreadcrumbs is typed as error when web-ui subpath can't be resolved by the eslint resolver
  return (
    <div className={rootClass}>
      {/* web-ui's prop is `aria-label`; the camelCase spelling is silently dropped. */}
      <WebUIBreadcrumbs items={items} renderLink={renderLink} css={navCss} aria-label={ariaLabel} />
    </div>
  )
}

export type { BreadcrumbsProps } from './breadcrumbs-ds.types'
